import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../../auth/authSlice";
import { enqueueSnackbar } from "notistack";
import { FaBarsStaggered, FaXmark } from "react-icons/fa6";
import { FaUserCircle } from "react-icons/fa";
import { API_BASE_URL } from "../../constants";
import PrimaryButton from "../../UI/PrimaryButton";
import cn from "classnames";
import { motion } from "framer-motion";
import logo from "../../assets/logos/svg/Garejelebi Logo variations-09.svg";

Modal.setAppElement("#root");

const Dropdown = ({ options }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isMobile = window.innerWidth < 768;

  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const dispatch = useDispatch();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogoutClick = () => {
    setIsModalOpen(true);
  };

  const handleProfileClick = () => {
    setIsOpen(false);
  };

  const handleConfirmLogout = async (event) => {
    event.preventDefault();
    setIsLoggingOut(true);
    setIsModalOpen(false);

    setIsOpen(false);

    try {
      const response = await fetch(`${API_BASE_URL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Logout failed:", errorData);
        return;
      }

      const data = await response.json();
      dispatch(clearUser());
      enqueueSnackbar(data.message, { variant: "success" });

      localStorage.removeItem("token");
      navigate("/");
    } catch (error) {
      console.error("Network or server error during logout:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleCancelLogout = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto"; // Enable scrolling
    }

    return () => {
      document.body.style.overflow = "auto"; // Reset on unmount
    };
  }, [isOpen]);

  return (
    <div className="" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center text-gray-800 focus:outline-none"
      >
        {isMobile ? (
          isOpen ? (
            <FaXmark className="text-2xl text-white" />
          ) : (
            <FaBarsStaggered className="text-2xl text-white" />
          )
        ) : (
          <FaUserCircle className="text-4xl text-white" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "tween", duration: 0.4 }}
          className={cn(
            "absolute flex flex-col right-0 top-14 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20",
            { "w-full mt-4 left-0 h-[100vh] overflow-y-auto items-center": isMobile }
          )}
        >
          {isAuthenticated && (
            <Link to="/profile">
              <button
                onClick={handleProfileClick}
                className="block px-4 py-4 text-baseBlue hover:bg-gray-100 w-full text-left"
              >
                {t("profile")}
              </button>
            </Link>
          )}

          {options.map((option) => (
            <Link
              key={option}
              to={"/" + option.toLowerCase()}
              className="block px-4 py-4 text-baseBlue hover:bg-gray-100"
              onClick={toggleDropdown}
            >
              {t(`${option}`)}
            </Link>
          ))}

          {isAuthenticated && (
            <button
              onClick={handleLogoutClick}
              className={cn(
                "block px-4 text-left py-2 text-baseBlue hover:bg-gray-100 w-full",
                {
                  "text-center": isMobile,
                }
              )}
            >
              {t("logOut")}
            </button>
          )}

          {isMobile && <img src={logo} alt="logo" className="h-20 mt-10" />}
        </motion.div>
      )}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleCancelLogout}
        className="absolute max-w-[95vw] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-8 rounded-lg shadow-xl z-50 w-96"
        overlayClassName="fixed inset-0 bg-gray-900 bg-opacity-50 z-40"
      >
        <h2 className="mb-6 text-2xl font-semibold text-gray-800">
          {t("areYouSure")}
        </h2>
        <div className="flex justify-end">
          <button
            onClick={handleCancelLogout}
            className=" px-4 py-2 mr-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {t("cancel")}
          </button>
          <div>
            <PrimaryButton
              text={isLoggingOut ? t("loggingOut") : t("logOut")}
              onClick={handleConfirmLogout}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dropdown;
